<!-- Shared templates: a submenu (Service/Administration) in its 3 variants, and a plain icon+label link. -->
<ng-template #submenuTpl let-menu="menu" let-mode="mode">
  @switch (mode) {
    @case ("mobile") {
      <a
        href="#{{ menu.id }}-mobile"
        data-bs-toggle="collapse"
        routerLinkActive="sidebar-link-active"
        class="nav-link align-middle dropdown-toggle">
        <i
          class="fa-solid {{ menu.icon }} nav-icon"
          routerLinkActive="sidebar-link-active"></i>
        <span class="ms-2">{{ menu.label | translate }}</span>
      </a>
      <ul
        [ngClass]="{
          collapse: true,
          show: expandedMenuIds().includes(menu.id),
        }"
        class="nav flex-column ms-4"
        id="{{ menu.id }}-mobile"
        data-bs-parent="#menu">
        @for (child of menu.children; track child.route; let isLast = $last) {
          <li class="w-100" [hidden]="!child.isVisible()">
            <a
              [routerLink]="child.route"
              routerLinkActive="sidebar-link-active"
              class="nav-link px-0">
              <span>{{ child.label | translate }}</span>
            </a>
          </li>
          @if (!isLast) {
            <hr class="my-1" [hidden]="!child.isVisible()" />
          }
        }
      </ul>
    }
    @case ("expanded") {
      <a
        href="#{{ menu.id }}-expanded"
        data-bs-toggle="collapse"
        routerLinkActive="sidebar-link-active"
        class="nav-link px-0 align-middle dropdown-toggle d-flex align-items-center">
        <i
          class="fa-solid {{ menu.icon }} nav-icon"
          routerLinkActive="sidebar-link-active"></i>
        @if (menu.hasBadge?.()) {
          <span
            style="left: 16%"
            class="position-absolute mb-3 translate-middle p-1 bg-success rounded-circle new-data-indicator"></span>
        }
        <span class="ms-2">{{ menu.label | translate }}</span>
      </a>
      <ul
        [ngClass]="{
          collapse: true,
          show: expandedMenuIds().includes(menu.id),
        }"
        class="nav flex-column ms-4"
        id="{{ menu.id }}-expanded"
        data-bs-parent="#menu">
        @for (child of menu.children; track child.route; let isLast = $last) {
          <li class="w-100" [hidden]="!child.isVisible()">
            <a
              [routerLink]="child.route"
              routerLinkActive="sidebar-link-active"
              [class]="child.linkClass || 'nav-link px-0'">
              <span [class]="child.spanClass || ''">{{
                child.label | translate
              }}</span>

              @if (child.hasBadge?.()) {
                <app-status-badge>
                  {{ "New" | translate }}
                </app-status-badge>
              }
            </a>
          </li>
          @if (!isLast) {
            <hr class="my-1" [hidden]="!child.isVisible()" />
          }
        }
      </ul>
    }
    @case ("collapsed") {
      <div class="dropdown dropend" style="white-space: nowrap">
        <a
          href="#{{ menu.id }}-collapsed"
          data-bs-toggle="dropdown"
          routerLinkActive="sidebar-link-active"
          class="nav-link px-0 align-middle">
          <i
            class="fa-solid {{ menu.icon }} nav-icon"
            routerLinkActive="sidebar-link-active"></i>
          @if (menu.hasBadge?.()) {
            <span
              class="position-absolute top-2 start-90 translate-middle p-1 bg-success rounded-circle new-data-indicator"></span>
          }
        </a>
        <ul
          class="dropdown-menu drop-end px-3 ms-1"
          id="{{ menu.id }}-collapsed">
          @for (child of menu.children; track child.route; let isLast = $last) {
            <li class="w-100" [hidden]="!child.isVisible()">
              <a
                [routerLink]="child.route"
                routerLinkActive="sidebar-link-active"
                [class]="child.linkClass || 'nav-link px-0'">
                <span [class]="child.spanClass || ''">{{
                  child.label | translate
                }}</span>

                @if (child.hasBadge?.()) {
                  <app-status-badge>
                    {{ "New" | translate }}
                  </app-status-badge>
                }
              </a>
            </li>
            @if (!isLast) {
              <hr class="my-1" [hidden]="!child.isVisible()" />
            }
          }
        </ul>
      </div>
    }
  }
</ng-template>

<ng-template #navItemTpl let-item="item" let-desktop="desktop">
  <a
    [routerLink]="item.route"
    routerLinkActive="sidebar-link-active"
    [class]="desktop ? 'nav-link px-0 align-middle' : 'nav-link align-middle'">
    <i
      class="fa-solid {{ item.icon }} nav-icon"
      routerLinkActive="sidebar-link-active"></i>
    @if (!desktop || !isCollapsed()) {
      <span class="ms-2">{{ item.label | translate }}</span>
    }
  </a>
</ng-template>

<!-- mobile -->
@if (isMobile) {
  <nav class="navbar navbar-expand-lg sidebar-gradient sidebar-glass d-lg-none">
    <div class="container-fluid">
      <a class="navbar-brand" routerLink="/portal/home">
        <img
          src="logo-only-first-letter.svg"
          routerLink="/portal/home"
          routerLinkActive="sidebar-link-active"
          alt="Logo Blum Novotest"
          class="my-auto img-fluid ms-1 cursor-pointer"
          style="width: 20px; height: 28px" />
      </a>
      <!-- Toggler -->
      <button
        class="navbar-toggler"
        type="button"
        data-bs-toggle="collapse"
        data-bs-target="#navbarNav"
        aria-controls="navbarNav"
        aria-expanded="false"
        aria-label="Toggle navigation">
        <span class="navbar-toggler-icon"></span>
      </button>
      <div class="collapse navbar-collapse" id="navbarNav">
        <ul class="navbar-nav">
          <!-- Dashboard -->
          <li class="nav-item">
            <a
              routerLink="/portal/home"
              class="nav-link d-flex align-items-center gap-2"
              routerLinkActive="sidebar-link-active">
              <div class="d-flex align-items-center">
                <i
                  class="fa-solid fa-gauge-high nav-icon"
                  routerLinkActive="sidebar-link-active"></i>
                <span class="ms-2">{{ "Dashboard" | translate }}</span>
              </div>

              @if (newsStateService.unreadNewsCount() > 0) {
                <span class="badge rounded-pill news-hint">
                  {{ newsStateService.unreadNewsCount() }}
                </span>
              }
            </a>
          </li>
          <!-- Support -->
          <li
            class="nav-item icon-alignment-expanded"
            [hidden]="!submenuVisible(supportMenu, 'mobile')">
            <ng-container
              *ngTemplateOutlet="
                submenuTpl;
                context: { menu: supportMenu, mode: 'mobile' }
              " />
          </li>

          <!-- Service -->
          <li
            class="nav-item icon-alignment-expanded"
            [hidden]="!submenuVisible(serviceMenu, 'mobile')">
            <ng-container
              *ngTemplateOutlet="
                submenuTpl;
                context: { menu: serviceMenu, mode: 'mobile' }
              " />
          </li>
          <!-- Simple links -->
          @for (item of simpleNavItems; track item.route) {
            <li class="nav-item" [hidden]="!item.isVisible()">
              <ng-container
                *ngTemplateOutlet="
                  navItemTpl;
                  context: { item: item, desktop: false }
                " />
            </li>
          }
          <!-- Admin -->
          <li
            class="nav-item"
            [hidden]="!submenuVisible(administrationMenu, 'mobile')">
            <ng-container
              *ngTemplateOutlet="
                submenuTpl;
                context: { menu: administrationMenu, mode: 'mobile' }
              " />
          </li>
          <hr class="my-1" />
          <!-- Profile -->
          <li class="nav-item">
            <a
              routerLink="/portal/profile"
              class="nav-link align-middle"
              routerLinkActive="sidebar-link-active">
              <i
                class="fa-solid fa-user nav-icon"
                routerLinkActive="sidebar-link-active"></i>
              <span class="ms-2">{{ "Profile" | translate }}</span>
            </a>
          </li>
          <!-- Darkmode -->
          <li class="nav-item">
            <app-user-dropdown />
          </li>
          <!-- Language -->
          <div class="nav-item dropdown">
            <app-language-picker />
          </div>
          <!-- Logout -->
          <li class="nav-item">
            <a
              href="#"
              (click)="logout($event)"
              (keydown)="logout($event)"
              aria-hidden="true"
              class="nav-link align-middle text-decoration-none">
              <i
                class="nav-icon fa-solid fa-arrow-right-from-bracket"
                style="color: #818b92; scale: 1.25"
                [ngClass]="iconCenterClass()"></i>
              <span class="ms-2">{{ "Logout" | translate }}</span>
            </a>
          </li>
        </ul>
      </div>
    </div>
  </nav>
} @else {
  <!-- web -->
  <nav
    id="web-sidebar"
    class="sidebar-gradient sidebar-glass d-none d-lg-block sticky-vertical position-fixed"
    [ngClass]="widthCssClass()">
    <div
      class="d-flex flex-column align-items-center pt-2 min-vh-100"
      [ngClass]="containerCenterCssClass()">
      <div
        class="d-flex flex-row align-items-center justify-content-center overflow-visible">
        @if (!isCollapsed()) {
          <img
            src="logo.svg"
            routerLink="/portal/home"
            routerLinkActive="sidebar-link-active"
            alt="Logo Blum Novotest"
            class="mt-3 img-fluid ms-4 cursor-pointer"
            style="width: 108px; height: 46px" />

          <div
            class="position-absolute drop-shadow sidebar-toggle-icon sidebar-glass"
            style="left: 260px"
            role="button"
            (click)="isCollapsed.set(!isCollapsed())"
            (keydown)="isCollapsed.set(!isCollapsed())"
            aria-disabled="true">
            <div
              class="d-flex flex-row justify-content-center align-items-center">
              <i class="fa-solid fa-caret-left"></i>
            </div>
          </div>
        } @else {
          <img
            src="logo-only-first-letter.svg"
            routerLink="/portal/home"
            routerLinkActive="sidebar-link-active"
            alt="Logo Blum Novotest"
            class="mt-3 img-fluid mx-auto cursor-pointer"
            style="width: 20px; height: 28px" />

          <div
            class="position-absolute drop-shadow sidebar-toggle-icon sidebar-glass"
            style="left: 65px; top: 26px"
            role="button"
            (click)="isCollapsed.set(!isCollapsed())"
            (keydown)="isCollapsed.set(!isCollapsed())"
            aria-disabled="true">
            <div
              class="d-flex flex-row justify-content-center align-items-center">
              <i class="fa-solid fa-caret-right"></i>
            </div>
          </div>
        }
      </div>

      @if (isCollapsed()) {
        <hr class="w-100 my-4 mx-0 text-white" />
      } @else {
        <hr class="w-100 my-4 mx-0" style="color: transparent" />
      }

      <ul
        class="nav flex-column mx-4 mb-auto mb-0 align-items-center justify-content-center"
        [ngClass]="containerCenterCssClass()"
        id="menu">
        <li class="nav-item pb-3" [ngClass]="iconCenterClass()">
          <a
            routerLink="/portal/home"
            class="nav-link px-0 align-middle d-flex align-items-center"
            routerLinkActive="sidebar-link-active">
            <div class="position-relative d-inline-block">
              <i
                class="fa-solid fa-gauge-high nav-icon"
                routerLinkActive="sidebar-link-active"></i>

              @if (newsStateService.unreadNewsCount() > 0) {
                <span
                  class="badge rounded-pill position-absolute top-0 start-100 translate-middle news-hint">
                  {{ newsStateService.unreadNewsCount() }}
                </span>
              }
            </div>

            @if (!isCollapsed()) {
              <span
                [ngClass]="
                  newsStateService.unreadNewsCount() > 0 ? 'ms-3' : 'ms-2'
                ">
                {{ "Dashboard" | translate }}
              </span>
            }
          </a>
        </li>
        <li
          class="nav-item pb-3"
          [ngClass]="iconCenterClass()"
          [hidden]="
            !submenuVisible(
              supportMenu,
              isCollapsed() ? 'collapsed' : 'expanded'
            )
          ">
          <ng-container
            *ngTemplateOutlet="
              submenuTpl;
              context: {
                menu: supportMenu,
                mode: isCollapsed() ? 'collapsed' : 'expanded',
              }
            " />
        </li>
        <li
          class="nav-item pb-3"
          [ngClass]="iconCenterClass()"
          [hidden]="
            !submenuVisible(
              serviceMenu,
              isCollapsed() ? 'collapsed' : 'expanded'
            )
          ">
          <ng-container
            *ngTemplateOutlet="
              submenuTpl;
              context: {
                menu: serviceMenu,
                mode: isCollapsed() ? 'collapsed' : 'expanded',
              }
            " />
        </li>
        @for (item of simpleNavItems; track item.route) {
          <li
            class="nav-item pb-3"
            [ngClass]="iconCenterClass()"
            [hidden]="!item.isVisible()">
            <ng-container
              *ngTemplateOutlet="
                navItemTpl;
                context: { item: item, desktop: true }
              " />
          </li>
        }
        <li
          class="nav-item pb-3"
          [ngClass]="iconCenterClass()"
          [hidden]="
            !submenuVisible(
              administrationMenu,
              isCollapsed() ? 'collapsed' : 'expanded'
            )
          ">
          <ng-container
            *ngTemplateOutlet="
              submenuTpl;
              context: {
                menu: administrationMenu,
                mode: isCollapsed() ? 'collapsed' : 'expanded',
              }
            " />
        </li>
      </ul>

      @if (!isCollapsed()) {
        <div class="mb-3 ms-4">
          <a
            href="#"
            (click)="logout($event)"
            (keydown)="logout($event)"
            style="color: #818b92"
            aria-hidden="true"
            class="nav-link align-middle text-decoration-none">
            <i
              class="fa-solid fa-arrow-right-from-bracket"
              [ngClass]="iconCenterClass()"
              style="color: #818b92; scale: 1.25"></i>

            <span class="ms-2">{{ "Logout" | translate }}</span>
          </a>
        </div>
      } @else {
        <div class="mb-3">
          <a
            href="#"
            (click)="logout($event)"
            (keydown)="logout($event)"
            aria-hidden="true"
            class="nav-link align-middle text-decoration-none">
            <i
              class="fa-solid fa-arrow-right-from-bracket"
              style="color: #818b92; scale: 1.25"
              [ngClass]="iconCenterClass()"></i>
          </a>
        </div>
      }

      @if (!isCollapsed()) {
        <div
          class="mb-4 ms-4"
          style="font-size: 0.75rem"
          [ngClass]="iconCenterClass()">
          <small> &copy; {{ currentYear }} - Blum-Novotest GmbH</small>
        </div>
      } @else {
        <div
          class="mb-4"
          style="font-size: 0.75rem"
          [ngClass]="iconCenterClass()">
          <small> &copy; {{ currentYear }} </small>
        </div>
      }
    </div>
  </nav>
}
